#!/usr/bin/env ts-node

/**
 * DDD Data Seeding Script
 * 
 * Creates consistent test data for DDD domain across all environments.
 * Follows Eric Evans Domain-Driven Design principles with strategic and tactical design.
 * Uses the same robust pattern as other domain packages.
 */

import { getMongoClient, getMongoConfig, initializeMongoConnection } from "@swoft/persistence";
import { DDD_COLLECTIONS } from '../config/collections';
import { v4 as uuidv4 } from 'uuid';

interface DDDTestData {
  organizationId: string;
  projectId: string;
  domains: Array<{
    name: string;
    description: string;
    vision: string;
    status: string;
  }>;
  boundedContexts: Array<{
    name: string;
    description: string;
    purpose: string;
    domainName: string;
    status: string;
  }>;
  aggregates: Array<{
    name: string;
    description: string;
    purpose: string;
    boundedContextName: string;
    status: string;
  }>;
  packageMappings: Array<{
    packageName: string;
    packageType: string;
    domainName: string;
    status: string;
    confidence: number;
  }>;
}

class DDDDataSeeder {
  private testData: DDDTestData = {
    organizationId: '2484554d-85d3-49a4-87bf-ceded4b115c0',
    projectId: 'c3f0fb44-79da-4200-b6c8-bd30ab57a6db',
    domains: [
      {
        name: 'Identity & Party Management',
        description: 'Core domain for user identity, authentication, and organizational party management',
        vision: 'Secure, scalable identity management enabling seamless user experiences across the platform',
        status: 'active'
      },
      {
        name: 'UX & Product Design',
        description: 'Strategic design domain for user experience research, persona development, and product design',
        vision: 'Data-driven design decisions through comprehensive user research and iterative design processes',
        status: 'active'
      },
      {
        name: 'AI Agent Framework',
        description: 'Core domain for AI agent orchestration, human-AI collaboration, and intelligent automation',
        vision: 'Seamless AI-human collaboration where agents augment human capabilities and decision-making',
        status: 'active'
      },
      {
        name: 'Developer Productivity',
        description: 'Supporting domain for GTD methodology, task management, and developer workflow optimization',
        vision: 'Maximize developer productivity through proven methodologies and intelligent automation',
        status: 'active'
      },
      {
        name: 'Platform Infrastructure',
        description: 'Technical domain for DevOps, deployment, monitoring, and platform reliability',
        vision: 'Zero-downtime deployments with comprehensive monitoring and automated recovery',
        status: 'active'
      }
    ],
    boundedContexts: [
      {
        name: 'Authentication',
        description: 'User authentication, session management, and access control',
        purpose: 'Secure user identity verification and session lifecycle management',
        domainName: 'Identity & Party Management',
        status: 'production'
      },
      {
        name: 'User Research',
        description: 'Persona development, user journey mapping, and experience design',
        purpose: 'Understanding user needs and behaviors to inform design decisions',
        domainName: 'UX & Product Design',
        status: 'production'
      },
      {
        name: 'AI Agent Orchestration',
        description: 'AI agent lifecycle management, task delegation, and human-AI coordination',
        purpose: 'Coordinate AI agents and human workflows for optimal productivity',
        domainName: 'AI Agent Framework',
        status: 'development'
      },
      {
        name: 'Getting Things Done (GTD)',
        description: 'David Allen GTD methodology implementation with inbox, projects, and actions',
        purpose: 'Implement proven productivity methodology for task and project management',
        domainName: 'Developer Productivity',
        status: 'production'
      },
      {
        name: 'Deployment Management',
        description: 'Automated deployment pipelines, environment management, and rollback strategies',
        purpose: 'Reliable, repeatable deployments with comprehensive validation and monitoring',
        domainName: 'Platform Infrastructure',
        status: 'production'
      },
      {
        name: 'Package Analysis',
        description: 'Monorepo package analysis, dependency mapping, and optimization recommendations',
        purpose: 'Optimize package organization and dependencies for maintainability and performance',
        domainName: 'Platform Infrastructure',
        status: 'development'
      }
    ],
    aggregates: [
      {
        name: 'Person',
        description: 'Person aggregate with identity, authentication, and organizational memberships',
        purpose: 'Manage person lifecycle, identity verification, and organizational relationships',
        boundedContextName: 'Authentication',
        status: 'implemented'
      },
      {
        name: 'Persona',
        description: 'User persona aggregate with research data, goals, and behavioral patterns',
        purpose: 'Encapsulate user research findings and persona characteristics for design decisions',
        boundedContextName: 'User Research',
        status: 'implemented'
      },
      {
        name: 'AIAgent',
        description: 'AI agent aggregate with capabilities, tasks, and collaboration patterns',
        purpose: 'Manage AI agent lifecycle, task assignment, and human-AI collaboration workflows',
        boundedContextName: 'AI Agent Orchestration',
        status: 'designed'
      },
      {
        name: 'Project',
        description: 'GTD project aggregate with outcomes, actions, and completion tracking',
        purpose: 'Implement GTD project management with clear outcomes and next actions',
        boundedContextName: 'Getting Things Done (GTD)',
        status: 'implemented'
      },
      {
        name: 'DeploymentPipeline',
        description: 'Deployment pipeline aggregate with stages, validation, and rollback capabilities',
        purpose: 'Orchestrate deployment process with comprehensive validation and recovery',
        boundedContextName: 'Deployment Management',
        status: 'designed'
      },
      {
        name: 'PackageMapping',
        description: 'Package-to-domain mapping aggregate with analysis and optimization recommendations',
        purpose: 'Track package organization and provide architectural insights',
        boundedContextName: 'Package Analysis',
        status: 'implemented'
      }
    ],
    packageMappings: [
      {
        packageName: '@swoft/party-manager',
        packageType: 'domain',
        domainName: 'Identity & Party Management',
        status: 'aligned',
        confidence: 0.95
      },
      {
        packageName: '@swoft/ui-ux-design-domain',
        packageType: 'domain',
        domainName: 'UX & Product Design',
        status: 'aligned',
        confidence: 0.98
      },
      {
        packageName: '@swoft/gtd-domain',
        packageType: 'domain',
        domainName: 'Developer Productivity',
        status: 'aligned',
        confidence: 0.92
      },
      {
        packageName: '@swoft/ai-domain',
        packageType: 'domain',
        domainName: 'AI Agent Framework',
        status: 'aligned',
        confidence: 0.88
      },
      {
        packageName: '@swoft/ddd',
        packageType: 'domain',
        domainName: 'Platform Infrastructure',
        status: 'aligned',
        confidence: 0.90
      },
      {
        packageName: '@swoft/dev-ops',
        packageType: 'infrastructure',
        domainName: 'Platform Infrastructure',
        status: 'aligned',
        confidence: 0.94
      },
      {
        packageName: '@swoft/mongo',
        packageType: 'infrastructure',
        domainName: 'Platform Infrastructure',
        status: 'aligned',
        confidence: 0.96
      }
    ]
  };

  private async setupMongo(): Promise<void> {
    const mongoConfig = {
      uri: process.env.MONGODB_URL || 'mongodb://localhost:27017',
      dbName: process.env.MONGODB_DB_NAME || 'swoft_designer_dev'
    };
    
    console.log(`🔗 Connecting to database: ${mongoConfig.dbName}`);
    await initializeMongoConnection(mongoConfig);
  }

  async run(): Promise<void> {
    try {
      console.log('🚀 Starting DDD Data Seeding...\n');
      
      await this.setupMongo();
      
      // Step 1: Clear existing data
      await this.clearExistingData();
      
      // Step 2: Seed domains (strategic design)
      const domainIds = await this.seedDomains();
      
      // Step 3: Seed bounded contexts (strategic design)
      const boundedContextIds = await this.seedBoundedContexts(domainIds);
      
      // Step 4: Seed aggregates (tactical design)
      await this.seedAggregates(boundedContextIds);
      
      // Step 5: Seed package mappings (implementation infrastructure)
      await this.seedPackageMappings(domainIds);
      
      // Step 6: Verify data integrity
      await this.verifyDataIntegrity();
      
      console.log('\n✅ DDD Data Seeding Completed Successfully!');
      console.log(`🏗️  Strategic Design: ${this.testData.domains.length} domains, ${this.testData.boundedContexts.length} bounded contexts`);
      console.log(`⚙️  Tactical Design: ${this.testData.aggregates.length} aggregates`);
      console.log(`📦 Implementation: ${this.testData.packageMappings.length} package mappings`);
      
    } catch (error) {
      console.error('❌ DDD seeding failed:', error);
      throw error;
    }
  }

  private async clearExistingData(): Promise<void> {
    console.log('🧹 Clearing existing DDD data...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    // Clear domains for this project
    await db.collection(DDD_COLLECTIONS.DOMAINS).deleteMany({
      projectId: this.testData.projectId
    });
    
    // Clear bounded contexts for this project
    await db.collection(DDD_COLLECTIONS.BOUNDED_CONTEXTS).deleteMany({
      projectId: this.testData.projectId
    });
    
    // Clear aggregates for this project
    await db.collection(DDD_COLLECTIONS.AGGREGATES).deleteMany({
      projectId: this.testData.projectId
    });
    
    // Clear package mappings for this project
    await db.collection(DDD_COLLECTIONS.PACKAGE_MAPPINGS).deleteMany({
      projectId: this.testData.projectId
    });
    
    console.log('✅ Existing data cleared');
  }

  private async seedDomains(): Promise<{ [name: string]: string }> {
    console.log('\n🏗️  Seeding domains (Strategic Design)...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    const domainIds: { [name: string]: string } = {};
    
    for (const domainData of this.testData.domains) {
      const domainId = uuidv4();
      
      const domain = {
        domainId: domainId,
        id: domainId,
        name: domainData.name,
        description: domainData.description,
        vision: domainData.vision,
        status: domainData.status,
        projectId: this.testData.projectId,
        organizationId: this.testData.organizationId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(DDD_COLLECTIONS.DOMAINS).insertOne(domain);
      domainIds[domainData.name] = domainId;
      
      console.log(`✅ Created domain: ${domainData.name}`);
    }
    
    return domainIds;
  }

  private async seedBoundedContexts(domainIds: { [name: string]: string }): Promise<{ [name: string]: string }> {
    console.log('\n🔲 Seeding bounded contexts (Strategic Design)...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    const boundedContextIds: { [name: string]: string } = {};
    
    for (const contextData of this.testData.boundedContexts) {
      const contextId = uuidv4();
      const domainId = domainIds[contextData.domainName];
      
      const boundedContext = {
        boundedContextId: contextId,
        id: contextId,
        name: contextData.name,
        description: contextData.description,
        purpose: contextData.purpose,
        status: contextData.status,
        domainId: domainId,
        projectId: this.testData.projectId,
        organizationId: this.testData.organizationId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(DDD_COLLECTIONS.BOUNDED_CONTEXTS).insertOne(boundedContext);
      boundedContextIds[contextData.name] = contextId;
      
      console.log(`✅ Created bounded context: ${contextData.name} → ${contextData.domainName}`);
    }
    
    return boundedContextIds;
  }

  private async seedAggregates(boundedContextIds: { [name: string]: string }): Promise<void> {
    console.log('\n⚙️  Seeding aggregates (Tactical Design)...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    for (const aggregateData of this.testData.aggregates) {
      const aggregateId = uuidv4();
      const boundedContextId = boundedContextIds[aggregateData.boundedContextName];
      
      const aggregate = {
        aggregateId: aggregateId,
        id: aggregateId,
        name: aggregateData.name,
        description: aggregateData.description,
        purpose: aggregateData.purpose,
        status: aggregateData.status,
        boundedContextId: boundedContextId,
        projectId: this.testData.projectId,
        organizationId: this.testData.organizationId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(DDD_COLLECTIONS.AGGREGATES).insertOne(aggregate);
      
      console.log(`✅ Created aggregate: ${aggregateData.name} → ${aggregateData.boundedContextName}`);
    }
  }

  private async seedPackageMappings(domainIds: { [name: string]: string }): Promise<void> {
    console.log('\n📦 Seeding package mappings (Implementation Infrastructure)...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    for (const mappingData of this.testData.packageMappings) {
      const mappingId = uuidv4();
      const domainId = domainIds[mappingData.domainName];
      
      const packageMapping = {
        mappingId: mappingId,
        id: mappingId,
        packageName: mappingData.packageName,
        packageType: mappingData.packageType,
        domainId: domainId,
        status: mappingData.status,
        confidence: mappingData.confidence,
        projectId: this.testData.projectId,
        organizationId: this.testData.organizationId,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await db.collection(DDD_COLLECTIONS.PACKAGE_MAPPINGS).insertOne(packageMapping);
      
      console.log(`✅ Created package mapping: ${mappingData.packageName} → ${mappingData.domainName}`);
    }
  }

  private async verifyDataIntegrity(): Promise<void> {
    console.log('\n🧪 Verifying data integrity...');
    
    const mongoClient = getMongoClient();
    const config = getMongoConfig();
    const db = mongoClient.db(config?.dbName);
    
    // Verify domains
    const domainCount = await db.collection(DDD_COLLECTIONS.DOMAINS)
      .countDocuments({ projectId: this.testData.projectId });
    console.log(domainCount === 5 ? '✅ Domains verified (5 created)' : `❌ Domains mismatch (expected 5, got ${domainCount})`);
    
    // Verify bounded contexts
    const contextCount = await db.collection(DDD_COLLECTIONS.BOUNDED_CONTEXTS)
      .countDocuments({ projectId: this.testData.projectId });
    console.log(contextCount === 6 ? '✅ Bounded contexts verified (6 created)' : `❌ Bounded contexts mismatch (expected 6, got ${contextCount})`);
    
    // Verify aggregates
    const aggregateCount = await db.collection(DDD_COLLECTIONS.AGGREGATES)
      .countDocuments({ projectId: this.testData.projectId });
    console.log(aggregateCount === 6 ? '✅ Aggregates verified (6 created)' : `❌ Aggregates mismatch (expected 6, got ${aggregateCount})`);
    
    // Verify package mappings
    const mappingCount = await db.collection(DDD_COLLECTIONS.PACKAGE_MAPPINGS)
      .countDocuments({ projectId: this.testData.projectId });
    console.log(mappingCount === 7 ? '✅ Package mappings verified (7 created)' : `❌ Package mappings mismatch (expected 7, got ${mappingCount})`);
    
    console.log('\n📊 Database Summary:');
    console.log(`Database: ${config?.dbName}`);
    console.log(`Collections: ${Object.values(DDD_COLLECTIONS).join(', ')}`);
    console.log(`Project ID: ${this.testData.projectId}`);
    console.log(`Organization ID: ${this.testData.organizationId}`);
  }
}

// Execute if run directly
if (require.main === module) {
  const seeder = new DDDDataSeeder();
  seeder.run()
    .then(() => {
      console.log('\n🎉 DDD data is ready!');
      console.log('\n📐 DDD Implementation Summary:');
      console.log('• Strategic Design: 5 domains with 6 bounded contexts');
      console.log('• Tactical Design: 6 aggregates with clear responsibilities');
      console.log('• Implementation: 7 package mappings for architectural alignment');
      console.log('\n🚀 Ready to implement Eric Evans Domain-Driven Design!');
      process.exit(0);
    })
    .catch((error) => {
      console.error('\n💥 DDD seeding failed:', error);
      process.exit(1);
    });
}

export { DDDDataSeeder };